/**
 * DateBadge — compact calendar chip: an abbreviated weekday over a large day
 * numeral over an abbreviated month, stacked in a tinted block.
 *
 * A port of the design system's `components/DateBadge/DateBadge.jsx`, plus
 * the shape it needs in order to also be the date block on a `WorkshopList`
 * row. The two are genuinely different objects in the design, so the props
 * are a union rather than a pile of optional flags — a stretched block takes
 * a colour and nothing else, a standalone chip takes one of the five tones:
 *
 *  - The standalone chip is the badge's own scale: a fixed box, a bordered
 *    edge, and a sans day numeral at 22/30/40.
 *  - The stretched block is the list's scale, from the design's
 *    `components/WorkshopList/WorkshopList.jsx`: a fixed width with no
 *    border, wider labels, and a mono day numeral at 30/33/37. Its corner is
 *    the row's own corner less 4, so those numbers track
 *    `workshopListSizes`; a test in this folder pins the relation.
 *
 * The stretched block also takes an arbitrary colour, because the list tints
 * it by sub-region and sub-regions are open-ended — five named tones cannot
 * express them.
 *
 * Called as the design draws it — a tone, no stretching — it renders exactly
 * as designed.
 */
import type { AriaAttributes, CSSProperties, ReactElement } from 'react';

export type DateBadgeTone = 'moss' | 'water' | 'clay' | 'bark' | 'gold';
export type DateBadgeSize = 'sm' | 'md' | 'lg';

interface DateBadgeBase {
  /** Abbreviated weekday, e.g. "Thu". Omit to hide the top line. */
  weekday?: string;
  /** Day of month, e.g. 21 or "21". Rendered large in tabular figures. */
  day: string | number;
  /** Abbreviated month, e.g. "May". Omit to hide the bottom line. */
  month?: string;
  /** Size preset. Default "md". */
  size?: DateBadgeSize;
  'aria-hidden'?: AriaAttributes['aria-hidden'];
}

/** The block that sits inside a row. It sizes itself, so it takes no `style`
 *  — a caller able to override `flex` here could put back the dead band the
 *  caller-supplied width used to cause. */
interface StretchedBadgeProps extends DateBadgeBase {
  stretched: true;
  /** The block's fill, e.g. a sub-region's accent. Text is white over it. */
  color: string;
}

/** The standalone chip, as designed: one of five tones, and a `style` escape
 *  hatch for callers placing it in their own layout. */
interface ChipBadgeProps extends DateBadgeBase {
  stretched?: false;
  /** Colour tone of the block. Default "moss". */
  tone?: DateBadgeTone;
  /** Spelled out so that a chip cannot be handed a free colour: without it
   *  TypeScript allows any key belonging to either arm of the union. */
  color?: never;
  /** Extra inline styles merged onto the wrapper. */
  style?: CSSProperties;
}

export type DateBadgeProps = StretchedBadgeProps | ChipBadgeProps;

interface ToneColors {
  bg: string;
  border: string;
  sub: string;
  month: string;
  fg: string;
}

const TONES: Record<DateBadgeTone, ToneColors> = {
  moss: {
    bg: 'var(--nb-moss-600)',
    border: 'var(--nb-moss-700)',
    sub: 'var(--nb-moss-200)',
    month: 'var(--nb-moss-100)',
    fg: '#fff',
  },
  water: {
    bg: 'var(--nb-water-600)',
    border: 'var(--nb-water-700)',
    sub: 'var(--nb-water-200)',
    month: 'var(--nb-water-100)',
    fg: '#fff',
  },
  clay: {
    bg: 'var(--nb-clay-600)',
    border: 'var(--nb-clay-700)',
    sub: 'var(--nb-clay-100)',
    month: 'var(--nb-clay-50)',
    fg: '#fff',
  },
  bark: {
    bg: 'var(--nb-bark-700)',
    border: 'var(--nb-bark-800)',
    sub: 'var(--nb-bark-200)',
    month: 'var(--nb-bark-100)',
    fg: '#fff',
  },
  gold: {
    bg: 'var(--nb-gold)',
    border: 'var(--nb-gold-tan)',
    sub: 'var(--nb-bark-700)',
    month: 'var(--nb-bark-800)',
    fg: 'var(--nb-bark-900)',
  },
};

/** The standalone chip's scale. */
const CHIP_SIZES: Record<
  DateBadgeSize,
  { w: number; h: number; sub: number; day: number; month: number; radius: number }
> = {
  sm: { w: 46, h: 54, sub: 8, day: 22, month: 8.5, radius: 9 },
  md: { w: 56, h: 66, sub: 10, day: 30, month: 10.5, radius: 10 },
  lg: { w: 72, h: 86, sub: 12, day: 40, month: 13, radius: 12 },
};

/** The in-row block's scale — wider, larger numeral, squarer corner. */
const STRETCHED_SIZES: Record<
  DateBadgeSize,
  { w: number; label: number; day: number; radius: number }
> = {
  sm: { w: 56, label: 12, day: 30, radius: 10 },
  md: { w: 62, label: 12, day: 33, radius: 11 },
  lg: { w: 70, label: 13, day: 37, radius: 12 },
};

const FONT_MONO = '"JetBrains Mono", ui-monospace, monospace';
const FONT_SANS = '"DM Sans", system-ui, sans-serif';

const LINE: CSSProperties = { display: 'block', width: '100%', textAlign: 'center' };

interface BadgeMetrics {
  wrap: CSSProperties;
  weekday: CSSProperties;
  day: CSSProperties;
  month: CSSProperties;
  /** The row block reaches for the shared mono/tabular classes, which is how
   *  every other dashboard primitive asks for figures. */
  dayClass?: string;
}

/** An arbitrary fill always takes white text, the way the list's date block
 *  has always painted a sub-region accent. */
function overrideColors(color: string): ToneColors {
  return {
    bg: color,
    border: color,
    sub: 'rgba(255,255,255,.85)',
    month: 'rgba(255,255,255,.75)',
    fg: '#fff',
  };
}

const STACK: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};

function stretchedMetrics(size: DateBadgeSize, c: ToneColors): BadgeMetrics {
  const z = STRETCHED_SIZES[size];
  const label: CSSProperties = {
    ...LINE,
    fontSize: z.label,
    fontWeight: 700,
    letterSpacing: '.12em',
    textTransform: 'uppercase',
  };
  return {
    wrap: {
      ...STACK,
      flex: `0 0 ${z.w}px`,
      alignSelf: 'stretch',
      borderRadius: z.radius,
      background: c.bg,
      color: c.fg,
      padding: '8px 0',
    },
    weekday: { ...label, color: c.sub },
    day: {
      ...LINE,
      // Named here as well as through the class: `.nb-mono` is scoped to
      // `.nb-dashboards`, and this badge is exported for surfaces that may
      // sit outside it, where the class alone would fall back to sans.
      fontFamily: FONT_MONO,
      fontSize: z.day,
      lineHeight: 1,
      margin: '1px 0 2px',
    },
    month: { ...label, color: c.month },
    dayClass: 'nb-tabular nb-mono',
  };
}

function chipMetrics(
  size: DateBadgeSize,
  c: ToneColors,
  style: CSSProperties | undefined,
): BadgeMetrics {
  const z = CHIP_SIZES[size];
  return {
    wrap: {
      ...STACK,
      width: z.w,
      height: z.h,
      borderRadius: z.radius,
      background: c.bg,
      border: `1px solid ${c.border}`,
      flex: 'none',
      color: c.fg,
      ...style,
    },
    weekday: {
      ...LINE,
      fontFamily: FONT_MONO,
      fontSize: z.sub,
      fontWeight: 700,
      color: c.sub,
      textTransform: 'uppercase',
      letterSpacing: '.1em',
      textIndent: '.1em',
      marginBottom: 2,
    },
    day: {
      ...LINE,
      fontFamily: FONT_SANS,
      fontWeight: 800,
      fontSize: z.day,
      lineHeight: 1,
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: '-.02em',
    },
    month: {
      ...LINE,
      fontFamily: FONT_MONO,
      fontSize: z.month,
      fontWeight: 700,
      color: c.month,
      textTransform: 'uppercase',
      letterSpacing: '.07em',
      textIndent: '.07em',
      marginTop: 2,
    },
  };
}

export function DateBadge(props: DateBadgeProps): ReactElement {
  const { weekday, day, month, size = 'md' } = props;
  const m = props.stretched
    ? stretchedMetrics(size, overrideColors(props.color))
    : chipMetrics(size, TONES[props.tone ?? 'moss'], props.style);

  return (
    <div style={m.wrap} data-component="DateBadge" aria-hidden={props['aria-hidden']}>
      {weekday && <span style={m.weekday}>{weekday}</span>}
      <span className={m.dayClass} style={m.day}>
        {day}
      </span>
      {month && <span style={m.month}>{month}</span>}
    </div>
  );
}
